import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareMenu from "@/components/feature/ShareMenu";
import { newsList } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function NewsDetail() {
  const { id } = useParams();
  const news = newsList.find((n) => n.id === id);

  useDocumentMeta(
    news ? `${news.title} - 51动漫资讯` : "资讯详情 - 51动漫",
    news ? `${news.summary}（发布于 ${news.date}，作者：${news.author}）` : "资讯详情 - 51动漫"
  );

  const related = useMemo(() => {
    if (!news) return [];
    const byId = news.relatedIds
      .map((rid) => newsList.find((n) => n.id === rid))
      .filter(Boolean);
    const sameCat = newsList.filter(
      (n) => n.id !== news.id && n.category === news.category && !news.relatedIds.includes(n.id)
    );
    return [...byId, ...sameCat].slice(0, 4);
  }, [news]);

  if (!news) {
    return (
      <div className="w-full px-4 md:px-8 py-16 min-h-[60vh] flex flex-col items-center justify-center text-center">
        <span className="w-16 h-16 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center mb-4">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-bold text-xl text-foreground-950 mb-2">未找到该资讯</h1>
        <p className="text-sm text-foreground-500 mb-5">你访问的资讯可能已删除或链接有误。</p>
        <Link
          to="/news"
          className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "资讯", to: "/news" },
          { label: news.category },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 md:gap-12">
        {/* Article */}
        <article className="min-w-0">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-semibold mb-4">
            {news.category}
          </span>
          <h1 className="font-heading font-extrabold text-2xl md:text-4xl leading-snug text-foreground-950 mb-4">
            {news.title}
          </h1>

          <div className="flex items-center flex-wrap gap-x-5 gap-y-2 pb-5 mb-6 border-b border-background-200 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {news.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              发布于 {news.date}
            </span>
            <div className="ml-auto">
              <ShareMenu title={news.title} />
            </div>
          </div>

          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 mb-6">
            <img
              src={news.cover}
              alt={`${news.title} 配图`}
              title={`${news.title} - 51动漫`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <p className="text-base md:text-lg leading-relaxed text-foreground-800 font-medium mb-6">
            {news.summary}
          </p>

          <div className="space-y-5">
            {news.content.map((para, index) => (
              <p key={index} className="text-sm md:text-base leading-loose text-foreground-700">
                {para}
              </p>
            ))}
          </div>

          <div className="mt-8 p-5 rounded-lg bg-primary-50 border border-primary-100 text-sm text-primary-800 flex items-start gap-2">
            <i className="ri-information-line text-lg mt-0.5"></i>
            <span>本文内容为平台示范数据，仅用于展示资讯详情页效果，不代表真实新闻。</span>
          </div>
        </article>

        {/* Related */}
        <aside className="w-full">
          <div className="lg:sticky lg:top-24">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-links-line text-primary-500"></i>
              相关文章
            </h2>
            <div className="flex flex-col gap-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/detail/${item.id}`}
                  className="group flex gap-3 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title} 配图`}
                      title={`${item.title} - 51动漫`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-sm text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h3>
                    <span className="mt-1 block text-[11px] text-foreground-500">{item.date}</span>
                  </div>
                </Link>
              ))}
            </div>

            <Link
              to="/news"
              className="mt-4 flex items-center justify-center gap-1.5 h-11 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-list-check"></i>
              查看全部资讯
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}